import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Table,
  Button,
  Input,
  Tag,
  Typography,
  Space,
  Select,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { listPrompts } from '../services/api/prompts';

const { Title, Text } = Typography;

export default function Prompts() {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState(null);

  const load = useCallback(async () => {
    if (!keycloak?.token) return;
    setLoading(true);
    try {
      const rows = await listPrompts(keycloak, { search: search || undefined, kind: kind || undefined });
      setData(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [keycloak, search, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <Button type="link" onClick={() => navigate(`/prompts/${row.id}`)} style={{ padding: 0 }}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      key: 'kind',
      render: (k) => <Tag color={k === 'template' ? 'purple' : 'blue'}>{k}</Tag>,
    },
    {
      title: 'Used in',
      key: 'bindings',
      render: (_, row) => (
        <Text type="secondary">
          {row.binding_count || 0} surface{(row.binding_count || 0) === 1 ? '' : 's'}
        </Text>
      ),
    },
    {
      title: 'Preview',
      dataIndex: 'live_body_preview',
      key: 'preview',
      ellipsis: true,
      render: (t) => <Text type="secondary">{t || '—'}</Text>,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
    },
  ];

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Prompt Library</Title>
          <Text type="secondary">Reusable templates and versioned instance prompts</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/prompts/new')}>
          New prompt
        </Button>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search prompts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={load}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="All kinds"
          allowClear
          style={{ width: 160 }}
          value={kind}
          onChange={setKind}
          options={[
            { value: 'instance', label: 'Instance' },
            { value: 'template', label: 'Template' },
          ]}
        />
        <Button onClick={load}>Search</Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
}
